import React from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from 'hooks/redux'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import OutputNodePane from 'components/feature/NavigatorPanes/OutputNodePane'
import { getIsSimulating } from 'selectors/navigator'
import { DrawerDivider, Header, Paragraph } from 'components/patterns'
import { virtualFuseReset } from 'i18n/displayConstants/featureOutputs'
import strings from 'utils/strings'
import { themeColor } from 'utils/styles'
import {
  getPowerNetLevelSimulationValues,
  getIsSimulatedVirtualFuseOpen,
  getSimulatedVirtualFuseRetries,
  getTimeRemainingVirtualFuseReset,
} from 'selectors/feature/simulation'
import { getOutputVirtualFuseSettings } from 'selectors/feature/outputs'
import {
  updateSimulatedOutputCurrent,
  updateSimulatedValue,
} from 'actions/feature/simulation'
import { EvaluationIconStyled } from 'components/patterns/Icon/IconsStyled'
import { StyledSelectMenu } from 'components/feature/NavigatorPanes/editors/base/InputValueEditor/InputValueEditor.style'
import PowerNetLevelSimulator, {
  IgnitionStatusProps,
  PowerComponentSimulator,
} from '../PowerNetLevelSimulator'
import ConditionSimulator from '../ConditionSimulator/ConditionSimulator'

const OutputSimulator = (props) => {
  const { powerNetLevel, virtualFuseSetting } = props
  const isSimulating = useSelector(getIsSimulating)
  return (
    <OutputSimulatorContainer>
      <OutputNodePane {...props} />
      {isSimulating && virtualFuseSetting && (
        <>
          <DrawerDivider />
          <VirtualFuseSettingSimulator
            {...props}
            powerNetLevel={powerNetLevel}
          />
        </>
      )}
      {isSimulating && powerNetLevel >= 0 && (
        <>
          <StyledPowerNetLevelSimulator powerNetLevel={powerNetLevel} />
        </>
      )}
    </OutputSimulatorContainer>
  )
}

OutputSimulator.propTypes = {
  powerNetLevel: PropTypes.number,
  virtualFuseSetting: PropTypes.object,
}

export default OutputSimulator

const OutputSimulatorContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 45px);
`

const StyledPowerNetLevelSimulator = styled(PowerNetLevelSimulator)`
  ${StyledSelectMenu} {
    width: 50%;
  }
`

const VirtualFuseSettingSimulator = ({
  id,
  resetType,
  virtualFuseSetting,
  powerNetLevel,
  ...props
}) => {
  const dispatch = useDispatch()
  const resetVirtualFuse = virtualFuseReset(resetType)
  const simulatedValue = useSelector((state) =>
    state.getIn(['feature', 'simulations', 'values', id, 'currentAmps'], 0)
  )
  const simulatedFuseOpen = useSelector(getIsSimulatedVirtualFuseOpen, {
    outputId: id,
  })

  const { ignitionStatus } = useSelector(getPowerNetLevelSimulationValues)

  const { resetAttempts } = useSelector(getOutputVirtualFuseSettings, { id })
  const retries = useSelector(getSimulatedVirtualFuseRetries, { outputId: id })
  const timeRemainingMs = useSelector(getTimeRemainingVirtualFuseReset, {
    outputId: id,
  })
  const timeRemainingSeconds = Math.floor(timeRemainingMs / 1000)
  const minutes = Math.floor(timeRemainingSeconds / 60)
  const seconds = timeRemainingSeconds % 60
  const timeRemainingFormatted = `${minutes}m ${seconds
    .toString()
    .padStart(2, '0')}s`

  const handleUpdateSimulatedValue = (value = 0) => {
    dispatch(updateSimulatedOutputCurrent(id, value))
  }

  const handleUpdateIgnitionStatus = (uri, value) =>
    dispatch(updateSimulatedValue(uri, value))

  const handleResetSimulatedValue = () => {
    dispatch(updateSimulatedOutputCurrent(id, 0))
  }

  return (
    <>
      <Layout>
        <StyledHeader type="h4">20A Pin Virtual Fuse</StyledHeader>
        <StyledParagraph style={{ fontWeight: 'bold' }}>
          {strings.reset_virtual_fuse}: {resetVirtualFuse}
        </StyledParagraph>
        <StyledParagraph type="body">
          {strings.evaluation} = {simulatedValue}A
        </StyledParagraph>
        <StyledParagraph>
          Current state of fuse:{' '}
          {simulatedFuseOpen && <strong>{strings.tripped}</strong>}
          {!simulatedFuseOpen && <strong>{strings.on}</strong>}
          <IconLayout>
            <EvaluationIconStyled
              evaluation={!simulatedFuseOpen}
              usePaletteStyles
            />
          </IconLayout>
        </StyledParagraph>
      </Layout>
      <VirtualLoadSimulator>
        <ConditionSimulator
          defaultValue={0}
          uri={'ff'}
          maxValue={100.0}
          selectedInputDescription={strings.virtual_load}
          units="A"
          simulatedValue={simulatedValue}
          translations={[{ translation: 'x*0.05' }]}
          inputUri={'ff'}
          minValue={0}
          handleUpdateSimulatedValue={handleUpdateSimulatedValue}
          queryType={'virtualFuse'}
          resetSimulatedValue={handleResetSimulatedValue}
          canReset={simulatedValue !== 0}
          showEvaluation={false}
        />
      </VirtualLoadSimulator>

      <VirtualIgnitionSimulator>
        <PowerComponentSimulator
          {...IgnitionStatusProps}
          simulatedValue={ignitionStatus}
          updateSimulatedValue={handleUpdateIgnitionStatus}
        />
      </VirtualIgnitionSimulator>

      {resetVirtualFuse === strings.automatic && (
        <div>
          <StyledParagraph>
            <strong>{strings.available_reset_attempts}</strong>
            <br />
            {retries} of {resetAttempts}
          </StyledParagraph>
          <DrawerDivider />
          <StyledParagraph>
            <strong>{strings.reset_timer}</strong>
            <br />
            {timeRemainingFormatted}
          </StyledParagraph>
          <DrawerDivider />
        </div>
      )}
    </>
  )
}

VirtualFuseSettingSimulator.propTypes = {
  id: PropTypes.string.isRequired,
  resetType: PropTypes.string.isRequired,
  virtualFuseSetting: PropTypes.object.isRequired,
  powerNetLevel: PropTypes.number.isRequired,
}

const StyledHeader = styled(Header)`
  color: ${themeColor('blackSecondary')};
  margin-bottom: 4px;
`

const StyledParagraph = styled(Paragraph)`
  font-size: 12px;
  color: ${themeColor('blackSecondary')};
}`

const IconLayout = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  margin-left: 4px;

  && > svg {
    width: 100%;
    height: 100%;
  }
`

const VirtualLoadSimulator = styled.div``

const VirtualIgnitionSimulator = styled.div`
  margin-bottom: 20px;
  && > div {
    border-top: 0;
  }
  ${StyledSelectMenu} {
    width: 50%;
  }
`

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
`
